import {
  Button,
  Drawer,
  Tag,
  Typography,
  Avatar,
  Space,
  Skeleton,
  Empty,
  Table,
  Input,
  Tooltip,
} from "antd";
import { MailOutlined, AimOutlined, UserOutlined } from "@ant-design/icons";
import { Beacons } from "./BeaconColumns";
import { useLocation, useNavigate } from "react-router-dom";
import useAxiosPrivate from "@/context/UseAxiosPrivate";
import { useEffect, useMemo, useState } from "react";
import { errorNotification } from "@/utils/Notification";

// time-ago support
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

// ---------- Types ----------
export type BeaconAsset = {
  asset_id?: string;
  client_name?: string;
  registration?: string;
  technician?: string;
  movedByName?: string;
  movedByEmail?: string;
  created_at?: string;
  updated_at?: string;
  [k: string]: any;
};

export type BeaconAudits = {
  id: number;
  event: "ActivateVehicle" | "ReverseActivation" | string;
  old_values?: string;
  new_values?: string;
  created_at: string;
  movedByName?: string;
  movedByEmail?: string;
  // from SQL join (old/new JSON -> beacon_stores.name)
  previousStore?: string;
  currentStore?: string;
  Assets?: {
    previousAsset?: BeaconAsset | {};
    currentAsset?: BeaconAsset | {};
  };
};

// ---------- Helpers ----------
const EVENT_COLOR: Record<string, { tag: string }> = {
  ActivateVehicle: { tag: "green" },
  ReverseActivation: { tag: "red" },
};

interface AuditLogsDrawerProps {
  viewAuditLogsDrawer: boolean;
  setViewAuditLogsDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  beacons: Beacons;
  setBeaconsData: React.Dispatch<React.SetStateAction<Beacons | undefined>>;
}

const ViewAuditLogsDrawerTable: React.FC<AuditLogsDrawerProps> = ({
  viewAuditLogsDrawer,
  setViewAuditLogsDrawer,
  beacons,
  setBeaconsData,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [auditData, setAuditData] = useState<BeaconAudits[]>([]);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();

  const onClose = () => {
    setBeaconsData(undefined);
    setViewAuditLogsDrawer(false);
  };

  // Fetch audits when the drawer opens / beacon changes
  useEffect(() => {
    if (!viewAuditLogsDrawer || !beacons?.id) return;

    const controller = new AbortController();
    setLoading(true);

    axiosPrivate
      .get(`/api/v1/audits/beaconsmovements/${beacons.id}`, {
        signal: controller.signal,
      })
      .then((response: any) => response?.data?.data ?? [])
      .then((data: BeaconAudits[]) => {
        if (!controller.signal.aborted) setAuditData(data);
      })
      .catch((err: any) => {
        if (
          controller.signal.aborted ||
          err?.code === "ERR_CANCELED" ||
          err?.name === "CanceledError"
        )
          return;

        const errors = err?.response?.data?.errors;
        if (errors && errors?.[0]?.message === "token as expired") {
          errorNotification("There was an issue", `${errors?.[0]?.message}`, "topRight");
          navigate("/", { state: { from: location }, replace: true });
        } else if (Array.isArray(errors) && errors.length > 0) {
          errors.forEach((e: any) =>
            errorNotification("There was an issue", `${e.message || "Unknown error"}`, "topRight"),
          );
        } else {
          errorNotification("There was an issue", "No server response", "topRight");
          navigate("/", { state: { from: location }, replace: true });
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [viewAuditLogsDrawer, beacons?.id, axiosPrivate, navigate, location]);

  // Filter + sort (newest first)
  const dataSource = useMemo(() => {
    const base = [...auditData].sort((a, b) =>
      String(b.created_at).localeCompare(String(a.created_at)),
    );
    if (!query) return base;
    const q = query.toLowerCase();

    return base.filter((it) => {
      const prev = it.Assets?.previousAsset as BeaconAsset | undefined;
      const curr = it.Assets?.currentAsset as BeaconAsset | undefined;
      const fields = [
        it.event,
        it.movedByName || "",
        it.movedByEmail || "",
        it.previousStore || "",
        it.currentStore || "",
        prev?.registration || "",
        prev?.technician || "",
        prev?.movedByName || "",
        prev?.movedByEmail || "",
        curr?.registration || "",
        curr?.technician || "",
        curr?.movedByName || "",
        curr?.movedByEmail || "",
      ];
      return fields.some((f) => f.toLowerCase().includes(q));
    });
  }, [auditData, query]);

  // Asset cell renderer
  const renderAsset = (a?: BeaconAsset) => (
    <div>
      <div>
        <Typography.Text strong>Reg:</Typography.Text> {a?.registration || "—"}
      </div>
      <div>
        <Typography.Text strong>Technician:</Typography.Text> {a?.technician || "—"}
      </div>
      <div>
        <Typography.Text strong>Moved By:</Typography.Text> {a?.movedByName || "—"}
        {a?.movedByEmail && (
          <>
            {" "}
            <MailOutlined /> <a href={`mailto:${a.movedByEmail}`}>{a.movedByEmail}</a>
          </>
        )}
      </div>
    </div>
  );

  const columns: any[] = [
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 190,
      sorter: (a: BeaconAudits, b: BeaconAudits) =>
        String(a.created_at).localeCompare(String(b.created_at)),
      defaultSortOrder: "descend" as const,
      render: (v: string) => (
        <Tooltip title={dayjs(v).format("YYYY-MM-DD HH:mm:ss")}>
          <Typography.Text>{dayjs(v).fromNow()}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: "Event",
      dataIndex: "event",
      width: 140,
      render: (ev: string) => <Tag color={EVENT_COLOR[ev]?.tag || "default"}>{ev}</Tag>,
    },
    {
      title: "Moved By",
      key: "moved",
      width: 260,
      render: (_: any, r: BeaconAudits) => {
        const who = r.movedByName || "—";
        const initials = (who || "")
          .split(" ")
          .map((s) => s[0])
          .join("")
          .slice(0, 2)
          .toUpperCase();
        return (
          <Space wrap>
            <Avatar size={24}>{initials || <UserOutlined />}</Avatar>
            <Typography.Text>{who}</Typography.Text>
            {r.movedByEmail && (
              <>
                <MailOutlined /> <a href={`mailto:${r.movedByEmail}`}>{r.movedByEmail}</a>
              </>
            )}
          </Space>
        );
      },
    },
    {
      title: "Stores",
      children: [
        {
          title: "Previous Store",
          dataIndex: "previousStore",
          width: 180,
          render: (v: string) => v || "—",
        },
        {
          title: "Current Store",
          dataIndex: "currentStore",
          width: 180,
          render: (v: string) => v || "—",
        },
      ],
    },
    {
      title: "Previous Asset",
      key: "prevAsset",
      width: 360,
      render: (_: any, r: BeaconAudits) =>
        renderAsset(r.Assets?.previousAsset as BeaconAsset | undefined),
    },
    {
      title: "Current Asset",
      key: "currAsset",
      width: 360,
      render: (_: any, r: BeaconAudits) =>
        renderAsset(r.Assets?.currentAsset as BeaconAsset | undefined),
    },
  ];

  return (
    <Drawer
      title={
        <Space align="center">
          <AimOutlined />
          <span>
            Audit Logs for Beacon CID: <b>{beacons?.cid}</b>
          </span>
        </Space>
      }
      width={1100}
      onClose={onClose}
      open={viewAuditLogsDrawer}
      bodyStyle={{ paddingBottom: 80 }}
      style={{ backgroundColor: "white", transition: "transform 0.3s ease, opacity 0.3s ease" }}
      maskStyle={{ transition: "opacity 0.3s ease" }}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Close
          </Button>
        </div>
      }
    >
      {loading ? (
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          <Skeleton active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 2 }} />
        </Space>
      ) : dataSource.length === 0 ? (
        <Empty description="No audit events yet" />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <Typography.Text type="secondary">
              {dataSource.length} event{dataSource.length === 1 ? "" : "s"} • newest first
            </Typography.Text>
            <Input.Search
              allowClear
              placeholder="Filter by event / user / store / reg"
              style={{ width: 360 }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <Table
            rowKey={(r) => r.id}
            columns={columns}
            dataSource={dataSource}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            size="middle"
            bordered
            scroll={{ x: 1200 }}
          />
        </>
      )}
    </Drawer>
  );
};

export default ViewAuditLogsDrawerTable;
